import { Link, useLocation } from 'react-router-dom'
import { Music, LogOut, Menu, X, Users, ListMusic, Sun, Moon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useBandContext } from '@/contexts/BandContext'
import { Button } from '@/components/ui/button'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

const navItems = [
  { to: '/', label: 'Bands', icon: Users },
  { to: '/repertoire', label: 'Repertoire', icon: Music },
  { to: '/setlists', label: 'Setlists', icon: ListMusic },
]

const THEME_STORAGE_KEY = 'band-manager-theme'

type Theme = 'light' | 'dark'

const getInitialTheme = (): Theme => {
  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY)
  if (storedTheme === 'light' || storedTheme === 'dark') return storedTheme
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export const Header = () => {
  const { signOut } = useAuth()
  const { bands, selectedBandId, setSelectedBandId } = useBandContext()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }

  const handleSignOut = async () => {
    const { error } = await signOut()

    if (error) {
      toast.error(error.message)
    }

    setMenuOpen(false)
  }

  return (
    <header className='no-print sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80'>
      <div className='mx-auto flex w-full max-w-5xl items-center justify-between gap-2 p-3 sm:p-4'>
        <div className='flex items-center gap-2 text-sm font-semibold'>Band Manager</div>

        <nav className='hidden items-center gap-2 sm:flex'>
          <Select value={selectedBandId} onValueChange={setSelectedBandId}>
            <SelectTrigger className='w-48'>
              <SelectValue placeholder='Select band' />
            </SelectTrigger>
            <SelectContent>
              {bands.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {navItems.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  location.pathname === item.to && 'bg-accent text-accent-foreground',
                )}
              >
                <Icon className='h-4 w-4' />
                {item.label}
              </Link>
            )
          })}
          <Button
            variant='outline'
            size='icon'
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <Sun className='h-4 w-4' /> : <Moon className='h-4 w-4' />}
          </Button>
          <Button variant='outline' size='sm' onClick={handleSignOut} className='hidden sm:inline-flex'>
            <LogOut className='mr-2 h-4 w-4' />
            Sign out
          </Button>
        </nav>
        <Button
          variant='outline'
          size='sm'
          className='sm:hidden'
          onClick={() => setMenuOpen((open) => !open)}
          aria-label='Toggle navigation menu'
        >
          {menuOpen ? <X className='h-4 w-4' /> : <Menu className='h-4 w-4' />}
        </Button>
      </div>

      {menuOpen && (
        <div className='border-t bg-background sm:hidden'>
          <nav className='mx-auto grid w-full max-w-5xl gap-2 p-3'>
            <Select value={selectedBandId} onValueChange={setSelectedBandId}>
              <SelectTrigger>
                <SelectValue placeholder='Select band' />
              </SelectTrigger>
              <SelectContent>
                {bands.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {navItems.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    location.pathname === item.to && 'bg-accent text-accent-foreground',
                  )}
                >
                  <Icon className='h-4 w-4' />
                  {item.label}
                </Link>
              )
            })}
            <Button
              variant='outline'
              size='sm'
              onClick={toggleTheme}
              className='justify-start'
            >
              {theme === 'dark' ? <Sun className='h-4 w-4' /> : <Moon className='h-4 w-4' />}
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </Button>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleSignOut}
              className='h-auto w-full justify-start rounded-md px-3 py-2 text-sm font-normal text-muted-foreground underline-offset-4 hover:bg-accent hover:text-accent-foreground hover:underline'
            >
              <LogOut className='h-4 w-4' />
              Sign out
            </Button>
          </nav>
        </div>
      )}
    </header>
  )
}
